(() => {
  const MODEL_TAG_PATTERN = /^[A-Za-z0-9]{1,4}-[A-Za-z]{2,8}(_[A-Za-z0-9]+)?$/;
  // Some models have no hyphen (e.g. RDS for RDS-400/600). They can only be matched
  // against the product MODEL_NO metafield, never against titles.
  const LOOSE_MODEL_TAG_PATTERN = /^[A-Za-z]{2,8}$/;
  const GENERIC_TAGS = new Set([
    'pfa-free',
    'not-fs',
    'swym-disabled',
    'ribbon-exclude',
    'parts-available',
    'discontinued',
    'for-sale',
  ]);

  function escapeHtml(value) {
    return String(value || '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  function partsListingUrl(modelNo, partsCollection) {
    if (!modelNo) return '';
    const params = new URLSearchParams({
      'filter.p.tag': modelNo,
      'filter.p.product_type': 'Parts',
    });
    if (partsCollection) {
      params.set('filter.p.parts_collection', partsCollection);
    }
    return `/collections/all?${params.toString()}`;
  }

  function rank(product) {
    const type = (product.type || '').toLowerCase();
    const suffix = product.template_suffix || '';
    if (type === 'parts') return -1;
    if (suffix === 'parts') return 3;
    if (suffix === 'discontinued') return 2;
    if (suffix.indexOf('nonelectric') !== -1) return 0;
    return 1;
  }

  function matchesTag(product, tag) {
    const needle = tag.toLowerCase();
    const modelNo = (product.model_no || '').trim().toLowerCase();

    // MODEL_NO is authoritative. When a product has one, only an exact match counts;
    // otherwise SM-TA would also pick up SM-TA48KT (SM-TA_KT) from its title, SM-TAE would
    // pick up SM-TAE48SA Hana+Kana (SM-TAE_SA), SW-KA would pick up SW-KA52H/75H, and so on.
    if (modelNo) return modelNo === needle;

    // Exact model tags disambiguate legacy listings without MODEL_NO, including
    // collaborations whose display titles do not identify their tag suffix.
    const modelTags = (product.tags || []).map((value) => String(value).trim().toLowerCase())
      .filter((value) => MODEL_TAG_PATTERN.test(value));
    if (modelTags.length) return modelTags.includes(needle);

    // RDS is a model family without a hyphen. Require a numeric model boundary.
    if (tag.indexOf('-') === -1) {
      return LOOSE_MODEL_TAG_PATTERN.test(tag) &&
        new RegExp(`(^|[^A-Za-z0-9])${tag}-\\d+(?=$|[^A-Za-z])`, 'i').test(product.title || '');
    }

    const title = product.title || '';
    const escaped = tag.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    // Not followed by digits plus a letter suffix such as 48KT / 48SA / 52H.
    const titlePattern = new RegExp(
      `(^|[^A-Za-z])${escaped}(?!\\d+[A-Za-z])(?=\\d|/|$|[^A-Za-z])`,
      'i'
    );
    if (titlePattern.test(title)) return true;

    const cobrand = tag.match(/^([A-Za-z0-9]{1,4}-[A-Za-z]{2,8})_([A-Za-z0-9]+)$/);
    if (!cobrand) return false;
    const family = cobrand[1].replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const suffix = cobrand[2].replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const cobrandPattern = new RegExp(`(^|[^A-Za-z])${family}\\d+(_)?${suffix}(?=$|[^A-Za-z])`, 'i');
    return cobrandPattern.test(title);
  }

  function listingTag(product, fallbackTag, partTags) {
    const modelNo = (product.model_no || '').trim();
    if (modelNo) return modelNo;

    const candidates = (partTags || []).filter((tag) => matchesTag(product, tag));
    const familyOnly = candidates.filter((tag) => tag.indexOf('_') === -1);
    familyOnly.sort((a, b) => b.length - a.length);
    if (familyOnly.length) return familyOnly[0];
    candidates.sort((a, b) => b.length - a.length);
    if (candidates.length) return candidates[0];
    return fallbackTag;
  }

  const TYPE_PARTS_COLLECTION = {
    'vacuum insulated mugs': 'parts-vacuum-insulated-mugs',
    'vacuum insulated bottles': 'parts-vacuum-insulated-bottles',
    'vacuum insulated food jars': 'parts-vacuum-insulated-food-jars',
    'vacuum insulated tumblers': 'parts-vacuum-insulated-tumblers',
    'vacuum insulated lunch jars': 'parts-vacuum-insulated-lunch-jars',
    'rice cookers': 'parts-rice-cookers',
    'water boilers': 'parts-water-boilers-warmers',
    'water boilers & warmers': 'parts-water-boilers-warmers',
    breadmakers: 'parts-breadmakers',
    'coffee makers': 'parts-coffee-makers',
    'other electric products': 'parts-other-electric-products',
    'thermal carafes': 'parts-thermal-carafes',
    'commercial products': 'parts-commercial-products',
    'commercial products - water boilers': 'parts-commercial-products',
  };

  const NONELECTRIC_PARTS_COLLECTIONS = [
    'parts-vacuum-insulated-mugs',
    'parts-vacuum-insulated-bottles',
    'parts-vacuum-insulated-food-jars',
    'parts-vacuum-insulated-tumblers',
    'parts-vacuum-insulated-lunch-jars',
  ];

  function partsCollectionFor(product, fallback) {
    const mapped = TYPE_PARTS_COLLECTION[(product.type || '').toLowerCase()];
    return mapped || fallback;
  }

  function isCombinedListing(product) {
    const suffix = product.template_suffix || '';
    const title = product.title || '';
    const tags = (product.tags || []).map((tag) => String(tag).toLowerCase());
    if (suffix.indexOf('nonelectric') !== -1) return true;
    if (/^\d+(\.\d+)?\s*oz/i.test(title)) return true;
    if (tags.indexOf('for-sale') !== -1 && tags.indexOf('parts-available') === -1) return true;
    return false;
  }

  function familyKey(product, partTags) {
    const modelNo = (product.model_no || '').trim().toUpperCase();
    if (modelNo) return modelNo;
    const title = product.title || '';
    const match = title.match(/([A-Za-z]{1,4}-[A-Za-z]{2,8})(\d+)?(?:\/\d+)?([A-Za-z]{2,})?/);
    if (!match) return (product._tag || '').toUpperCase();
    const family = match[1].toUpperCase();
    const suffix = (match[3] || '').toUpperCase();
    if (suffix) {
      const cobrand = `${family}_${suffix}`;
      if ((partTags || []).some((tag) => tag.toUpperCase() === cobrand)) return cobrand;
    }
    return family;
  }

  function letterFamily(product, partTags) {
    return familyKey(product, partTags)
      .toUpperCase()
      .replace(/_.*$/, '')
      .replace(/\d+$/, '');
  }

  // Combined Listing singles ("30 oz. Stainless Carry Tumbler SX-LA89H") share their MODEL_NO
  // with the group listing ("Stainless Carry Tumbler SX-LA89H"). Show only the group.
  function sameModel(a, b, partTags) {
    const aModel = (a.model_no || '').trim().toUpperCase();
    const bModel = (b.model_no || '').trim().toUpperCase();
    if (aModel && bModel) return aModel === bModel;
    return letterFamily(a, partTags) === letterFamily(b, partTags);
  }

  function preferGroupShots(items, partTags) {
    const groups = items.filter((product) => !isCombinedListing(product));
    if (!groups.length) return items.filter((product) => !/^\d+(\.\d+)?\s*oz\b/i.test(product.title || ''));
    return items.filter(
      (product) =>
        !/^\d+(\.\d+)?\s*oz\b/i.test(product.title || '') &&
        (!isCombinedListing(product) || !groups.some((group) => sameModel(product, group, partTags)))
    );
  }

  async function fetchJsonView(url) {
    try {
      const response = await fetch(url, { credentials: 'same-origin' });
      if (!response.ok) return null;
      const text = await response.text();
      const match = text.trim().match(/\[[\s\S]*\]/);
      if (!match) return null;
      const data = JSON.parse(match[0]);
      return Array.isArray(data) ? data : null;
    } catch (error) {
      return null;
    }
  }

  async function fetchProductsForTag(tag) {
    const data = await fetchJsonView(
      `/search?q=${encodeURIComponent(tag)}&type=product&view=applicable-models`
    );
    return data || [];
  }

  function buildCard(product, options) {
    const title = escapeHtml(product.title);
    const image = product.image
      ? `<img src="${escapeHtml(product.image)}" alt="${title}" class="motion-reduce" loading="lazy" width="${product.image_width || 533}" height="${product.image_height || 533}">`
      : `<svg class="placeholder-svg" viewBox="0 0 525 525" xmlns="http://www.w3.org/2000/svg"></svg>`;
    const url = escapeHtml(product.url || '#');

    return `
      <li class="grid__item">
        <div class="card-wrapper product-card-wrapper underline-links-hover applicable-products__card">
          <div class="card card--${escapeHtml(options.cardStyle)} ${product.image ? 'card--media' : 'card--text'}">
            <div class="card__inner ${options.cardStyle === 'standard' ? 'color-' + options.cardColorScheme + ' gradient' : ''} ratio" style="--ratio-percent: 100%;">
              <div class="card__media">
                <div class="media media--transparent">
                  <a href="${url}" class="applicable-products__media-link" aria-hidden="true" tabindex="-1">${image}</a>
                </div>
              </div>
            </div>
            <div class="card__content">
              <div class="card__information applicable-products__info">
                <h3 class="card__heading h5 applicable-products__heading">
                  <a href="${url}" class="full-unstyled-link">${title}</a>
                </h3>
              </div>
            </div>
          </div>
        </div>
      </li>
    `;
  }

  function normalizeImage(src) {
    if (!src) return null;
    if (src.indexOf('//') === 0) return `https:${src}`;
    return src;
  }

  function mapAjaxProduct(product) {
    const image = product.featured_image || (product.images && product.images[0]) || null;
    const imageSrc = typeof image === 'string' ? image : image && (image.src || image);
    const tags = Array.isArray(product.tags) ? product.tags : String(product.tags || '').split(',');
    const normalizedTags = tags.map((tag) => String(tag).trim()).filter(Boolean);
    return {
      id: product.id,
      title: product.title,
      handle: product.handle,
      type: product.product_type || product.type || '',
      template_suffix: normalizedTags.some((tag) => tag.toLowerCase() === 'discontinued') ? 'discontinued' : '',
      model_no: '',
      tags: normalizedTags,
      image: normalizeImage(imageSrc),
      image_width: image && image.width ? image.width : 720,
      image_height: image && image.height ? image.height : 720,
    };
  }

  async function fetchProductByHandle(handle) {
    try {
      const response = await fetch(`/products/${encodeURIComponent(handle)}.js`, {
        credentials: 'same-origin',
      });
      if (!response.ok) return null;
      return mapAjaxProduct(await response.json());
    } catch (error) {
      return null;
    }
  }

  function handleForTag(tag) {
    return tag.replace(/-/g, '').toLowerCase();
  }

  async function fetchCollectionProducts(handle) {
    if (!handle) return [];
    const viewed = await fetchJsonView(
      `/collections/${encodeURIComponent(handle)}?view=applicable-models`
    );
    if (viewed) return viewed;

    try {
      const response = await fetch(`/collections/${encodeURIComponent(handle)}/products.json?limit=250`, {
        credentials: 'same-origin',
      });
      if (!response.ok) return [];
      const data = await response.json();
      return (data.products || []).map(mapAjaxProduct);
    } catch (error) {
      return [];
    }
  }

  async function enhanceFallback(root) {
    const grid = root.querySelector('[data-applicable-grid]');
    if (!grid || grid.dataset.mode !== 'fallback') return;

    const allTags = (grid.dataset.modelTags || '')
      .split(',')
      .map((tag) => tag.trim())
      .filter((tag) => tag && !GENERIC_TAGS.has(tag.toLowerCase()));
    const tags = allTags.filter((tag) => MODEL_TAG_PATTERN.test(tag));
    const looseTags = allTags.filter((tag) => LOOSE_MODEL_TAG_PATTERN.test(tag));
    const matchTags = tags.concat(looseTags);

    if (!matchTags.length) return;

    const currentId = grid.dataset.currentId;
    const partsCollection = grid.dataset.partsCollection || '';
    const unique = new Map();
    const parentCollection = partsCollection.replace(/^parts-/, '');

    const crossCategory =
      !partsCollection || NONELECTRIC_PARTS_COLLECTIONS.indexOf(partsCollection) !== -1
        ? NONELECTRIC_PARTS_COLLECTIONS
        : [];
    const collectionHandles = Array.from(
      new Set([parentCollection, partsCollection].concat(crossCategory).filter(Boolean))
    );

    const groups = await Promise.all([
      ...matchTags.map((tag) => fetchProductsForTag(tag)),
      ...matchTags.map((tag) => fetchProductByHandle(handleForTag(tag))),
      ...collectionHandles.map((handle) => fetchCollectionProducts(handle)),
    ]);
    const searchGroups = groups.slice(0, matchTags.length);
    const handleProducts = groups.slice(matchTags.length, matchTags.length * 2);
    const collectionProducts = groups.slice(matchTags.length * 2).flat();

    // Products fetched through /products/x.js or products.json carry no MODEL_NO. Borrow it from
    // the same product in the search / collection views so matching never has to guess.
    const modelIndex = new Map();
    searchGroups
      .flat()
      .concat(collectionProducts)
      .forEach((product) => {
        if (product && product.model_no) modelIndex.set(String(product.id), product.model_no);
      });
    const withModel = (product) => {
      if (product && !product.model_no && modelIndex.has(String(product.id))) {
        product.model_no = modelIndex.get(String(product.id));
      }
      return product;
    };
    handleProducts.forEach(withModel);
    collectionProducts.forEach(withModel);

    const addProduct = (product, tag) => {
      if (!product || String(product.id) === String(currentId)) return;
      if (rank(product) < 0) return;
      if (!matchesTag(product, tag)) return;

      const key = String(product.id);
      const existing = unique.get(key);
      const scored = Object.assign({}, existing || {}, product, {
        _rank: rank(product),
        _tag: tag,
        model_no: product.model_no || (existing && existing.model_no) || '',
      });
      if (!existing || scored._rank >= existing._rank) {
        unique.set(key, scored);
      } else if (scored.model_no && !existing.model_no) {
        existing.model_no = scored.model_no;
      }
    };

    searchGroups.forEach((products, index) => {
      products.forEach((product) => addProduct(product, matchTags[index]));
    });
    handleProducts.forEach((product, index) => {
      if (product) addProduct(product, matchTags[index]);
    });
    collectionProducts.forEach((product) => {
      const matchedTag = matchTags.find((tag) => matchesTag(product, tag));
      if (matchedTag) addProduct(product, matchedTag);
    });

    const merged = Array.from(unique.values()).map((product) => {
      const collection = partsCollectionFor(product, partsCollection);
      return Object.assign(product, {
        url:
          partsListingUrl(listingTag(product, product._tag, matchTags), collection) ||
          `/products/${product.handle}`,
      });
    });
    const items = preferGroupShots(merged, matchTags).sort((a, b) => a.title.localeCompare(b.title));
    if (!items.length) return;

    const options = {
      cardStyle: grid.dataset.cardStyle || 'standard',
      cardColorScheme: grid.dataset.cardColorScheme || 'background-2',
    };

    grid.innerHTML = items.map((product) => buildCard(product, options)).join('');
    root.classList.remove('applicable-products--hidden');
    root.style.display = 'block';
  }

  function init() {
    document.querySelectorAll('[data-applicable-root]').forEach((root) => {
      enhanceFallback(root).catch(() => {});
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
